/**
 * Màn **Lịch đăng** — *"sắp đăng gì, site nào, mấy giờ, chặn kịp không?"*
 *
 * Ô số đáng nhìn nhất **không phải** "sắp đăng", mà là **"quá hạn"**: dòng lịch có giờ đã trôi qua
 * mà `daChay` vẫn `false`. Đó là bài **kẹt** — hoặc cron đang tắt, hoặc job hỏng. Trong bảng
 * `schedule` thô nó trông y hệt một bài sắp đăng, và đó là cách một bài nằm im vài ngày mà không
 * ai biết.
 *
 * Và mỗi dòng nói rõ **chế độ thực tế của site đó**: `dry` nghĩa là tới giờ sẽ **không có gì rời
 * khỏi máy này**. Không hiện chuyện đó thì `dry` trông hệt `live`, và người xem tưởng bài đã lên
 * website khách hàng thật.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  DangTai,
  DanhSach,
  DauTrang,
  Hang,
  Khoi,
  Nut,
  Doc,
  HuyHieuLive,
  LuoiDoc,
  Rong,
  The,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

/** Còn bao lâu nữa tới giờ đăng — hoặc đã quá bao lâu. */
function conBaoLau(iso: string): string {
  const d = new Date(iso).getTime() - Date.now();
  const phut = Math.round(Math.abs(d) / 60000);
  const chuoi =
    phut < 60 ? `${phut} phút` : phut < 1440 ? `${Math.round(phut / 60)} giờ` : `${Math.round(phut / 1440)} ngày`;
  return d >= 0 ? `còn ${chuoi}` : `quá ${chuoi}`;
}

export function LichDang() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isPending, error } = useQuery({ queryKey: khoa.lich, queryFn: () => api.lich(100) });

  const lamMoi = () => {
    void qc.invalidateQueries({ queryKey: khoa.lich });
    void qc.invalidateQueries({ queryKey: khoa.tongQuan });
    void qc.invalidateQueries({ queryKey: khoa.hangCho });
  };

  const xepLich = useMutation({
    mutationFn: (articleId: string) => api.lichXep(articleId),
    onSuccess: (kq) => {
      toast.on(
        `Đã xếp lịch — web lúc ${gioVN(kq.gioWeb)}`,
        kq.gioFb ? `Facebook lúc ${gioVN(kq.gioFb)}.` : "Bài này KHÔNG lên Facebook.",
      );
      lamMoi();
    },
    onError: (loi: Error) => toast.loi("Không xếp được lịch", loi.message),
  });

  const dangNgay = useMutation({
    mutationFn: (id: string) => api.lichDangNgay(id),
    onSuccess: (kq) => {
      // ⚠️ Job chạy xong mà `loi` khác null vẫn là **hỏng**. Hiện xanh cho nó là dạy người đọc
      // rằng màu xanh không có nghĩa gì — và đó là cách một lần đăng hỏng đi qua mà không ai thấy.
      if (kq.loi !== null) toast.loi("Đăng ngay: HỎNG", `${kq.ghiChu} — ${kq.loi}`);
      else if (kq.daChay) toast.on("Đã đăng", kq.ghiChu);
      else toast.bao("thuong", "Không có gì để đăng", kq.ghiChu);
      lamMoi();
    },
    onError: (loi: Error) => toast.loi("Đăng ngay không chạy được", loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Lịch đăng" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Lịch đăng" />
        <DangTai />
      </div>
    );
  }

  const sapToi = data.muc.filter((m) => !m.daChay).sort((a, b) => a.gioDang.localeCompare(b.gioDang));
  const xong = data.muc.filter((m) => m.daChay);

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Lịch đăng"
        trangThai={<HuyHieuLive live={data.publishMode === "live"} />}
        moTa="AutoIME tự giữ lịch. Không giao cho Website IME — API bên đó bỏ qua publishedAt tương lai và bài sẽ lên ngay."
      >
        <Nut kieu="vien" onClick={lamMoi}>
          Làm mới
        </Nut>
      </DauTrang>

      {/* Kết quả xếp lịch / đăng ngay đi bằng toast — bảng lịch bên dưới dài, và dòng vừa thao
          tác thường không nằm ở đầu trang. */}

      <LuoiDoc cot={3}>
        <Doc nhan="Sắp đăng" giaTri={data.sapDang} />
        {/*
          Ô đáng nhìn nhất màn này — bài KẸT, không phải bài sắp đăng. Đỏ chứ không cam: cam nghĩa
          là "cần anh làm gì đó", đỏ nghĩa là "đã hỏng", và một dòng lịch quá giờ mà chưa chạy
          thuộc vế thứ hai.
        */}
        <Doc
          nhan="Quá hạn chưa chạy"
          giaTri={data.quaHan}
          sacThai={data.quaHan > 0 ? "xau" : "tot"}
        />
        <Doc nhan="Đã chạy" giaTri={data.daChay} />
      </LuoiDoc>

      {data.quaHan > 0 && (
        <Bao muc="loi" tieuDe={`${data.quaHan} dòng lịch đã quá giờ mà chưa chạy`}>
          {data.cronBat ? (
            <>
              Cron đang bật nên job lẽ ra phải chạy — nghĩa là job đang <strong>hỏng</strong>. Xem
              cột lỗi ở màn Chi phí, hoặc cảnh báo trên Tổng quan.
            </>
          ) : (
            <>
              Cron đang <strong>TẮT</strong>, nên không có gì tự chạy. Bật ở{" "}
              <Link to="/cai-dat" className="underline">
                Cài đặt → <code>job.chayTuDong</code>
              </Link>
              , hoặc bấm &ldquo;Chạy một lượt&rdquo; ở Tổng quan.
            </>
          )}
        </Bao>
      )}

      {!data.cronBat && data.quaHan === 0 && (
        <Bao muc="canh" tieuDe="Cron đang tắt">
          Bài đã duyệt sẽ được xếp lịch nhưng <strong>không tự đăng</strong> — phải bấm &ldquo;Chạy
          một lượt&rdquo;. Đây là trạng thái an toàn, không phải lỗi.
        </Bao>
      )}

      {data.chuaXepLich.length > 0 && (
        <The>
          <div className="mb-1 text-sm font-medium text-muc-800">
            {data.chuaXepLich.length} bài đã duyệt nhưng CHƯA có lịch
          </div>
          <p className="mb-2 text-2xs text-muc-500">
            Bình thường lúc bấm Duyệt là xếp lịch luôn. Nằm ở đây nghĩa là lần xếp đó hỏng — bài
            đang đứng ở <code>APPROVED</code>, có trên bộ đếm, và <strong>không ai xử lý</strong>.
          </p>
          <DanhSach>
            {data.chuaXepLich.map((b) => (
              <Hang key={b.articleId}>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 px-4 py-2.5">
                  <span className="truncate text-sm text-muc-800" title={b.tieuDe ?? ""}>
                    {b.tieuDe ?? "(không tiêu đề)"}
                  </span>
                  {b.siteSlug && <Chip muc="thuong">{b.siteSlug}</Chip>}
                  <div className="ml-auto flex shrink-0 gap-2">
                    <Nut
                      kieu="chinh"
                      // `isPending` một mình làm MỌI hàng cùng quay — `variables` giữ đúng id
                      // vừa gửi, nên chỉ hàng vừa bấm mới báo đang chạy.
                      dangChay={xepLich.isPending && xepLich.variables === b.articleId}
                      onClick={() => xepLich.mutate(b.articleId)}
                    >
                      Xếp lịch
                    </Nut>
                    <Link
                      to={`/bai/${b.articleId}`}
                      className="self-center text-2xs text-muc-500 underline hover:text-muc-800"
                    >
                      xem bài
                    </Link>
                  </div>
                </div>
              </Hang>
            ))}
          </DanhSach>
        </The>
      )}

      <Khoi
        nhan="Sắp tới"
        id="lich-sap-toi"
        moSan
        tomTat={
          sapToi.length === 0 ? (
            <span className="text-muc-500">không có gì đang chờ</span>
          ) : (
            `${sapToi.length} dòng lịch`
          )
        }
      >
      <The>
        {sapToi.length === 0 ? (
          <Rong tieuDe="Không có gì đang chờ đăng">
            Duyệt một bài ở Hàng chờ thì nó xuất hiện ở đây.
          </Rong>
        ) : (
          <DanhSach>
            {sapToi.map((m) => (
              <Hang key={m._id}>
                <div className="min-w-0 flex-1 px-4 py-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-muc-900" title={m.tieuDe ?? ""}>
                        {m.tieuDe ?? <span className="text-muc-400">(bài đã bị xoá)</span>}
                      </div>
                      <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                        <Chip muc={m.dich === "FACEBOOK" ? "canh" : "thuong"}>{m.dich}</Chip>
                        {m.siteSlug && <Chip muc="thuong">{m.siteSlug}</Chip>}
                        <Chip muc={m.cheDoThucTe === "live" ? "canh" : "thuong"}>
                          {m.cheDoThucTe === "live" ? "sẽ đăng THẬT" : "dry — không gửi đi đâu"}
                        </Chip>
                        {m.quaHan && <Chip muc="loi">quá hạn</Chip>}
                        {m.trangThaiBai && <Chip muc="thuong">{m.trangThaiBai}</Chip>}
                      </div>
                      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 text-2xs text-muc-500">
                        <span className={m.quaHan ? "text-loi-600" : ""}>{conBaoLau(m.gioDang)}</span>
                        <span className="text-muc-300">·</span>
                        <span>{gioVN(m.gioDang)}</span>
                        {/* Trễ vài phút mà CHƯA tính là quá hạn: cron chạy theo chu kỳ, nên
                            "đang chờ tới lượt" và "kẹt" là hai chuyện khác nhau. */}
                        {!m.quaHan && new Date(m.gioDang).getTime() < Date.now() && (
                          <>
                            <span className="text-muc-300">·</span>
                            <span className="text-muc-400">đang chờ lượt cron</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <Nut
                        kieu={m.cheDoThucTe === "live" ? "nguyHiem" : "chinh"}
                        dangChay={dangNgay.isPending && dangNgay.variables === m._id}
                        onClick={() => dangNgay.mutate(m._id)}
                      >
                        {m.cheDoThucTe === "live" ? "Đăng THẬT ngay" : "Đăng ngay (dry)"}
                      </Nut>
                      <Link
                        to={`/bai/${m.articleId}`}
                        className="text-2xs text-muc-500 underline hover:text-muc-800"
                      >
                        xem bài
                      </Link>
                    </div>
                  </div>
                </div>
              </Hang>
            ))}
          </DanhSach>
        )}
        <p className="mt-3 text-2xs text-muc-500">
          &ldquo;Đăng ngay&rdquo; kéo giờ về hiện tại rồi chạy <strong>chính job đó</strong> — cùng
          một đường code với cron, không có nhánh riêng. Nó <strong>không</strong> bỏ qua chế độ:
          ở <code>dry</code> vẫn chạy hết dây chuyền và ghi kết quả, chỉ không gửi đi đâu.
        </p>
      </The>

      </Khoi>

      <Khoi
        nhan="Đã chạy"
        id="lich-da-chay"
        tomTat={
          xong.length === 0 ? (
            <span className="text-muc-500">chưa lần nào</span>
          ) : (
            `${xong.length} lần đăng`
          )
        }
      >
      <The>
        {xong.length === 0 ? (
          <Rong tieuDe="Chưa lần đăng nào chạy">Không phải lỗi — chỉ là chưa tới giờ nào.</Rong>
        ) : (
          <DanhSach>
            {xong.map((m) => (
              <Hang key={m._id}>
                <div className="min-w-0 flex-1 px-4 py-2.5">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <Chip muc={m.ketQua === null ? "canh" : m.ketQua.thanhCong ? "on" : "loi"}>
                      {m.ketQua === null ? "không có kết quả" : m.ketQua.thanhCong ? "ok" : "hỏng"}
                    </Chip>
                    <Chip muc="thuong">{m.dich}</Chip>
                    {m.ketQua && (
                      <Chip muc={m.ketQua.cheDo === "live" ? "canh" : "thuong"}>
                        {m.ketQua.cheDo === "live" ? "ĐÃ ĐĂNG THẬT" : "dry — mô phỏng"}
                      </Chip>
                    )}
                    <span className="truncate text-sm text-muc-700" title={m.tieuDe ?? ""}>
                      {m.tieuDe ?? "(bài đã bị xoá)"}
                    </span>
                    <span className="ml-auto shrink-0 text-2xs text-muc-500">{gioVN(m.gioDang)}</span>
                  </div>

                  {m.ketQua !== null && (
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 text-2xs text-muc-500">
                      {m.ketQua.imeSlug && (
                        <span>
                          slug <code>{m.ketQua.imeSlug}</code>
                          {m.ketQua.cheDo !== "live" && (
                            <span className="text-muc-400"> (đề nghị, chưa phải slug thật)</span>
                          )}
                        </span>
                      )}
                      {m.ketQua.fbPostId && <span>fb {m.ketQua.fbPostId}</span>}
                      {m.dich === "FACEBOOK" &&
                        m.ketQua.thanhCong &&
                        m.ketQua.fbCommentId === null &&
                        m.ketQua.cheDo === "live" && (
                          <span className="text-loi-600">
                            ⚠️ bài đã lên nhưng COMMENT CHỨA LINK HỎNG — đường duy nhất về web
                          </span>
                        )}
                    </div>
                  )}

                  {m.ketQua?.loi && (
                    <p className="mt-1 min-w-0 break-words text-2xs text-loi-600">{m.ketQua.loi}</p>
                  )}
                </div>
              </Hang>
            ))}
          </DanhSach>
        )}
      </The>
      </Khoi>

      <p className="text-2xs text-muc-500">
        Website đăng <strong>trước</strong>, Facebook sau, đệm tối thiểu 10 phút — dưới ngưỡng đó là
        đăng link trước khi trang kịp dựng (<code>revalidate = 300</code>), và người bấm vào thấy
        404 trong khi mọi bảng đều báo thành công.
      </p>
    </div>
  );
}
